import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/db8cd846oi3g3tv8ul9g';
const MAX_MESSAGE = 500;

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [messageLen, setMessageLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honey = String(data.get('website_alt') || '').trim();
    if (honey) {
      setStatus('success');
      setMessage('提交成功，感谢你的留言，我们会尽快回复。');
      form.reset();
      setMessageLen(0);
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim()) payload.append(key, value);
    });

    try {
      setStatus('submitting');
      setMessage('');
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (!isOk || isSpam) {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试。');
        return;
      }
      setStatus('success');
      setMessage('提交成功，感谢你的留言，我们会尽快回复。');
      form.reset();
      setMessageLen(0);
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-form-seyoyo"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground-900">
        <i className="ri-mail-send-line text-primary-500"></i>
        联系我们 / 投稿
      </h3>
      <p className="mt-1 text-sm text-foreground-500">有任何建议、合作或投稿，欢迎给我们留言。</p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
            你的称呼
          </label>
          <input
            id="cf-name"
            name="name"
            type="text"
            required
            placeholder="请输入称呼"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
            邮箱
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-topic" className="mb-1.5 block text-sm font-medium text-foreground-700">
          留言类型
        </label>
        <select
          id="cf-topic"
          name="topic"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
        >
          <option value="内容建议">内容建议</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="新闻投稿">新闻投稿</option>
          <option value="问题反馈">问题反馈</option>
        </select>
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between">
          <label htmlFor="cf-message" className="block text-sm font-medium text-foreground-700">
            留言内容
          </label>
          <span className="text-xs text-foreground-400">{messageLen}/{MAX_MESSAGE}</span>
        </div>
        <textarea
          id="cf-message"
          name="message"
          required
          maxLength={MAX_MESSAGE}
          rows={5}
          onChange={(e) => setMessageLen(e.target.value.length)}
          placeholder="请填写你的留言内容（500字以内）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
        ></textarea>
      </div>

      <input
        className="form-extra-field"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 cursor-pointer whitespace-nowrap sm:w-auto"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中...
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交留言
            </>
          )}
        </button>

        {status === 'success' && (
          <p className="flex items-center gap-1.5 text-sm text-primary-700">
            <i className="ri-checkbox-circle-line"></i>
            {message}
          </p>
        )}
        {status === 'error' && (
          <p className="flex items-center gap-1.5 text-sm text-accent-700">
            <i className="ri-error-warning-line"></i>
            {message}
          </p>
        )}
      </div>
    </form>
  );
}